import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, Check, ChevronDown, ChevronUp, Loader2, Minus, Plus } from 'lucide-react';
import { useClickOutside } from '@/hooks/useClickOutside';
import {
  DetailPageDesignerData,
  DetailPagePlannerData,
  DetailPageStage,
  SplitPrompt,
} from '@/types';
import { useAgentStore, FALLBACK_LLM_MODELS } from '@/stores/agentStore';
import { useModelStore, FALLBACK_IMAGE_MODELS, DEFAULT_IMAGE_MODEL_ID } from '@/stores/modelStore';
import { useSelectionStore } from '@/stores/selectionStore';
import { useNodeSkillSession } from '../../hooks/useNodeSkillSession';
import { formatBlockLabel, resolveBlockDisplayTitle } from '../../hooks/useDetailPageActions';
import { DetailPageFormRow } from './detail-page';
import { DetailPageSkillPanel } from './DetailPageSkillPanel';
import { PlannerResultPanel } from './PlannerResultPanel';
import { SplitPromptFullscreenView } from './SplitPromptFullscreenView';

type SkillSession = ReturnType<typeof useNodeSkillSession>;

/** 品牌色（与 MultiAnglePanel / LightingPanel 同源） */
const ACCENT = '#6D5BF5';

/** 启动卡风格下拉精选选项：从后端 page_design「风格速查表」34 项中精选常用 12 项，避免下拉过长 */
const STYLE_OPTIONS = [
  '简约ins风', '新中式风', '北欧风', '日式极简风', '韩系甜美风', '国潮风',
  '科技感', '清新自然风', '萌趣卡通风', '复古怀旧风', '轻奢风', '杂志大片风',
];

/** 设计稿三步执行轨道：架构策划 → 分屏提示词 → 拆分出图 */
const PIPELINE_STEPS = ['架构策划', '分屏提示词', '拆分出图'] as const;

const UNIT_TYPE_LABELS: Record<SplitPrompt['unitType'], string> = {
  S: '首屏',
  D: '详情',
  T: '尾屏',
};

/* ---------- 统一设计 token：控件 h-14 / rounded-xl / text-[22px]，行标题 text-[24px] white/45，辅助文字 text-[18px] white/35，行距 gap-5 ---------- */

interface DropdownOption {
  value: string;
  label: string;
}

/** 节点内自定义下拉：参照 AgentModelSelector 菜单；菜单经 portal 挂到 body（脱离卡片 overflow-hidden 裁切），按视口剩余空间自适应向上/向下弹出 */
const NodeDropdown: React.FC<{
  value: string;
  options: DropdownOption[];
  onChange: (value: string) => void;
  placeholder: string;
  className?: string;
}> = ({ value, options, onChange, placeholder, className = 'w-[300px]' }) => {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{
    left: number;
    top: number;
    width: number;
    maxHeight: number;
  } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const closeMenu = useCallback(() => setOpen(false), []);
  useClickOutside([rootRef, menuRef], closeMenu, open);

  useEffect(() => {
    if (!open) return;
    const onWheel = (e: WheelEvent) => {
      if (menuRef.current?.contains(e.target as Node)) return;
      closeMenu();
    };
    window.addEventListener('wheel', onWheel, true);
    window.addEventListener('resize', closeMenu);
    return () => {
      window.removeEventListener('wheel', onWheel, true);
      window.removeEventListener('resize', closeMenu);
    };
  }, [open, closeMenu]);

  const toggleMenu = () => {
    if (open) {
      setOpen(false);
      return;
    }
    const rect = rootRef.current?.getBoundingClientRect();
    if (!rect) return;
    const GAP = 8;
    const spaceAbove = rect.top - GAP;
    const spaceBelow = window.innerHeight - rect.bottom - GAP;
    const openUp = spaceAbove >= spaceBelow;
    const maxHeight = Math.min(280, openUp ? spaceAbove : spaceBelow);
    setMenuPos({
      left: rect.left,
      top: openUp ? rect.top - GAP - maxHeight : rect.bottom + GAP,
      width: rect.width,
      maxHeight,
    });
    setOpen(true);
  };
  const stop = (e: React.MouseEvent) => e.stopPropagation();
  const selected = options.find((opt) => opt.value === value);

  return (
    <div ref={rootRef} className={`relative ${className}`} onMouseDown={stop}>
      <button
        type="button"
        onClick={toggleMenu}
        className="h-14 w-full px-5 rounded-xl bg-white/[0.05] border border-white/[0.10] text-[18px] outline-none focus:border-white/25 hover:border-white/20 transition-colors flex items-center justify-between gap-2 text-left"
      >
        <span className={`truncate ${selected ? 'text-white/85' : 'text-white/40'}`}>
          {selected?.label ?? placeholder}
        </span>
        <ChevronDown
          size={20}
          className={`shrink-0 text-white/45 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open &&
        menuPos &&
        createPortal(
          <div
            ref={menuRef}
            className="fixed z-[9999] overflow-y-auto rounded-xl"
            style={{
              left: menuPos.left,
              top: menuPos.top,
              width: menuPos.width,
              maxHeight: menuPos.maxHeight,
              background: '#141414',
              border: '1px solid rgba(255,255,255,0.10)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
            }}
          >
            {options.map((opt) => {
              const active = opt.value === value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  className={`w-full h-12 px-5 flex items-center justify-between gap-3 text-left text-[18px] transition-colors ${
                    active ? 'bg-[#2d2d2d] text-white' : 'text-white/75 hover:bg-[#2d2d2d]'
                  }`}
                >
                  <span className="truncate">{opt.label}</span>
                  {active && <Check size={18} color={ACCENT} strokeWidth={3} className="shrink-0" />}
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </div>
  );
};

interface DetailPagePipelineViewProps {
  stage: DetailPageStage;
  planner: DetailPagePlannerData | undefined;
  designer: DetailPageDesignerData | undefined;
  generatedBlocks: number[];
  plannerSession: SkillSession;
  designerSession: SkillSession;
  onUpdatePlanner: (updates: Partial<DetailPagePlannerData>) => void;
  onSyncProductImageNodes: (count: number) => void;
  onSyncCompetitorImageNodes: (count: number) => void;
  onSkipCompetitor: () => void;
  onSyncSellingPointNode: (mode: 'auto' | 'manual') => void;
  onRunPlanner: () => void;
  onRunDesigner: () => void;
  /** E3 重跑分屏提示词：清空出图记录/旧切块并回写 stage=DESIGNING 后重新发起 */
  onRegeneratePrompts: () => void;
  onGenerateBlock: (block: SplitPrompt) => void;
  onGenerateAll: () => void;
  /** 节点标题（策划架构面板头部展示用） */
  nodeTitle: string;
}

const DetailPagePipelineViewInner: React.FC<DetailPagePipelineViewProps> = ({
  stage,
  planner,
  designer,
  generatedBlocks,
  plannerSession,
  designerSession,
  onUpdatePlanner,
  onSyncProductImageNodes,
  onSyncCompetitorImageNodes,
  onSkipCompetitor,
  onSyncSellingPointNode,
  onRunPlanner,
  onRunDesigner,
  onRegeneratePrompts,
  onGenerateBlock,
  onGenerateAll,
  nodeTitle,
}) => {
  const current = planner ?? {
    productImageCount: 0,
    productImageNodeIds: [] as string[],
    sellingPoints: [],
    sellingPointNodeIds: [] as string[],
    skipCompetitor: false,
    competitorImageCount: 0,
    competitorImageNodeIds: [] as string[],
    style: '',
    model: '',
    imageModel: '',
    advancedOpen: false,
    hasArchitectureTable: false,
    isPlannerComplete: false,
  };

  // 高级设置展开态持久化在 planner 中：节点高度随其动态伸缩（FlowEditor / DetailPageNode 双源读取）
  const advancedOpen = current.advancedOpen ?? false;
  // 策划架构查看面板 + 分屏「重新生成」二次确认（DirectorPanel 同款 useState 模式）
  const [plannerPanelOpen, setPlannerPanelOpen] = useState(false);
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);
  // 分屏提示词「查看」弹窗：行内不再折叠，点击查看经全屏面板展示提示词全文（面板框架与策划架构查看面板一致）
  const [viewingPrompt, setViewingPrompt] = useState<SplitPrompt | null>(null);

  // E12：面板打开时清除画布全部选中态，避免全屏面板下误触 Backspace/Delete 删除画布选中节点
  useEffect(() => {
    if (plannerPanelOpen || viewingPrompt) useSelectionStore.getState().clearSelection();
  }, [plannerPanelOpen, viewingPrompt]);

  // 模型列表与智能体同源；planner.model 为空 = 跟随全局设置
  const availableModels = useAgentStore((s) => s.availableModels);
  const ensureAvailableModels = useAgentStore((s) => s.ensureAvailableModels);
  const globalModelId = useAgentStore((s) => s.currentModelId);
  useEffect(() => {
    ensureAvailableModels();
  }, [ensureAvailableModels]);
  const models = availableModels.length > 0 ? availableModels : FALLBACK_LLM_MODELS;
  const effectiveModelId = current.model || globalModelId;
  const effectiveModelName = models.find((m) => m.id === effectiveModelId)?.name ?? effectiveModelId;

  // 生图模型列表与图片生成节点编辑面板同源（GET /api/models 按 txt2img 过滤 + 供应商可见性）；imageModel 为空 = 跟随默认
  const allApiModels = useModelStore((s) => s.models);
  const providerVisibility = useModelStore((s) => s.providerVisibility);
  const loadModels = useModelStore((s) => s.loadModels);
  useEffect(() => {
    loadModels().catch(() => {});
  }, [loadModels]);
  const txt2imgModels = allApiModels.filter(
    (m) => m.supportedModes.includes('txt2img') && providerVisibility[m.backend] !== false,
  );
  const imageModels = txt2imgModels.length > 0 ? txt2imgModels : FALLBACK_IMAGE_MODELS;
  const defaultImageModelName =
    imageModels.find((m) => m.id === DEFAULT_IMAGE_MODEL_ID)?.name ?? DEFAULT_IMAGE_MODEL_ID;

  // 会话未挂载/已复位时回退到持久化 runState（画布重挂载恢复场景）
  const effectivePlannerRunState = plannerSession.runState.status === 'idle' && planner?.runState
    ? planner.runState
    : plannerSession.runState;
  const effectiveDesignerRunState = designerSession.runState.status === 'idle' && designer?.runState
    ? designer.runState
    : designerSession.runState;

  const prompts = designer?.splitPrompts ?? [];

  // V5：planner 会话 style_confirmation 挂起时，面板底部显示「确认方案」按钮（等同弹窗确认，回复后推进 designer）
  const styleConfirmPending =
    effectivePlannerRunState.status === 'waiting_ask' && effectivePlannerRunState.currentStep === 'style_confirmation'
      ? { currentStep: 'style_confirmation' as const }
      : null;

  const isConfig = stage === DetailPageStage.CONFIG;
  const isRunningStage = stage === DetailPageStage.PLANNING || stage === DetailPageStage.DESIGNING;
  const isConsume = stage === DetailPageStage.CONSUME;
  const railIndex = stage === DetailPageStage.PLANNING ? 0 : 1;
  const plannerBusy =
    effectivePlannerRunState.status === 'running' || effectivePlannerRunState.status === 'waiting_ask';

  const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

  /** 标题行状态徽标（§4.2）：统一灰底胶囊，按 runState/stage 五态判定（跨 planner/designer 会话取整体状态） */
  const renderStatusPill = () => {
    const busy =
      effectivePlannerRunState.status === 'running' || effectiveDesignerRunState.status === 'running';
    const waiting =
      effectivePlannerRunState.status === 'waiting_ask' ||
      effectiveDesignerRunState.status === 'waiting_ask';
    const errored =
      effectivePlannerRunState.status === 'error' || effectiveDesignerRunState.status === 'error';
    const label = busy ? '执行中' : waiting ? '待确认' : errored ? '失败' : isConsume ? '已完成' : '待配置';
    return (
      <span className="h-11 px-5 rounded-full bg-white/[0.06] border border-white/15 text-[20px] text-white/75 flex items-center gap-2 shrink-0">
        {busy && <Loader2 size={20} className="animate-spin shrink-0" />}
        {waiting && <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: ACCENT }} />}
        {errored && <AlertCircle size={20} className="text-red-400 shrink-0" />}
        {!busy && !waiting && !errored && isConsume && <Check size={20} className="text-emerald-400 shrink-0" />}
        {label}
      </span>
    );
  };

  const renderHead = () => (
    <div
      className="flex items-center gap-3 px-12 pt-9 pb-3 shrink-0"
      onMouseDown={stopPropagation}
    >
      <span className="w-3 h-3 rounded-full shrink-0" style={{ background: ACCENT }} />
      <span className="text-[26px] font-medium text-white/95 truncate">
        {isConsume ? '详情页 · 结果' : '详情页 · 流水线'}
      </span>
      <span className="flex-1 min-w-0" />
      {renderStatusPill()}
    </div>
  );

  const renderRail = () => (
    <div className="flex items-center gap-3 px-12 pt-1 pb-2 shrink-0" onMouseDown={stopPropagation}>
      {PIPELINE_STEPS.map((label, i) => {
        const done = i < railIndex;
        const active = i === railIndex;
        return (
          <React.Fragment key={label}>
            {i > 0 && (
              <div
                className="flex-1 h-px"
                style={{ background: done ? ACCENT : 'rgba(255,255,255,0.12)' }}
              />
            )}
            <div className="flex items-center gap-2 shrink-0">
              <span
                className="relative w-5 h-5 rounded-full border-[1.5px] flex items-center justify-center"
                style={{ borderColor: done || active ? ACCENT : 'rgba(255,255,255,0.18)' }}
              >
                {done && <Check size={13} color={ACCENT} strokeWidth={3} />}
                {active && (
                  <>
                    {[-0.8, 0].map((delay) => (
                      <span
                        key={delay}
                        className="absolute rounded-full pointer-events-none"
                        style={{
                          inset: -5,
                          border: `1.5px solid ${ACCENT}`,
                          animation: `dpRailRing 1.6s ease-out ${delay}s infinite`,
                        }}
                      />
                    ))}
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: ACCENT }} />
                  </>
                )}
              </span>
              <span
                className={`text-[22px] ${active ? 'font-medium' : ''}`}
                style={{
                  color: active ? ACCENT : done ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.32)',
                }}
              >
                {label}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );

  const renderSummaryPills = () => {
    // 汇总展示：面板管理数量 + 手动连线接入数量
    const productTotal = current.productImageCount + (current.connectedProductImageNodeIds?.length ?? 0);
    const competitorTotal = current.competitorImageCount + (current.connectedCompetitorImageNodeIds?.length ?? 0);
    const pills = [
      `产品图 ${productTotal}`,
      current.skipCompetitor || competitorTotal === 0
        ? '竞品 跳过'
        : `竞品 ${competitorTotal} 份`,
      `风格 ${current.style || '智能推荐'}`,
    ];
    if (prompts.length > 0) pills.push(`区块数 ${prompts.length}`);
    return (
      <div className="flex flex-wrap gap-3">
        {pills.map((label) => (
          <span
            key={label}
            className="h-11 px-5 rounded-full bg-white/[0.08] text-[20px] text-white/75 flex items-center gap-2"
          >
            {label}
            <Check size={18} color={ACCENT} strokeWidth={3} />
          </span>
        ))}
      </div>
    );
  };

  const renderStepper = (
    count: number,
    onChange: (next: number) => void,
    min: number,
    max: number,
    /** 数量为 0 时中间显示的占位文案（如竞品图显示「跳过」）；不传则显示数字 0 */
    zeroLabel?: string,
    /** 展示数（默认同 count）：面板数与手动连线数合并显示；加减按钮仍只操作面板数 count */
    displayCount?: number,
  ) => {
    const shown = displayCount ?? count;
    return (
    <div className="flex items-center gap-4">
      <button
        type="button"
        onClick={() => {
          const next = Math.max(min, count - 1);
          if (next !== count) onChange(next);
        }}
        disabled={count <= min}
        className="w-14 h-14 rounded-xl border border-white/[0.10] bg-white/[0.05] text-white/80 hover:bg-white/[0.10] hover:border-white/20 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center shrink-0"
      >
        <Minus size={22} />
      </button>
      <span
        className={`min-w-12 text-center font-medium text-white/95 ${
          shown === 0 && zeroLabel ? 'text-[20px]' : 'text-[26px] tabular-nums'
        }`}
      >
        {shown === 0 && zeroLabel ? zeroLabel : shown}
      </span>
      <button
        type="button"
        onClick={() => {
          const next = Math.min(max, count + 1);
          if (next !== count) onChange(next);
        }}
        disabled={count >= max}
        className="w-14 h-14 rounded-xl border border-white/[0.10] bg-white/[0.05] text-white/80 hover:bg-white/[0.10] hover:border-white/20 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center shrink-0"
      >
        <Plus size={22} />
      </button>
    </div>
    );
  };

  const renderStyleRow = () => (
    <NodeDropdown
      className="w-48"
      value={current.style}
      placeholder="手动选择风格…"
      onChange={(style) => onUpdatePlanner({ style })}
      options={[
        { value: '', label: '智能推荐' },
        ...(!current.style || STYLE_OPTIONS.includes(current.style)
          ? []
          : [{ value: current.style, label: current.style }]),
        ...STYLE_OPTIONS.map((style) => ({ value: style, label: style })),
      ]}
    />
  );

  const renderConfigBody = () => {
    // 展示口径与手柄指示器/汇总一致：面板管理数 + 手动连线数；加减按钮仍只管理面板图（连线由画布拖拽增删）
    const connectedProductCount = current.connectedProductImageNodeIds?.length ?? 0;
    const connectedCompetitorCount = current.connectedCompetitorImageNodeIds?.length ?? 0;
    const competitorShown = current.skipCompetitor ? 0 : current.competitorImageCount + connectedCompetitorCount;
    return (
    <section className="flex flex-col gap-5">
      <DetailPageFormRow label="产品图">
        {renderStepper(
          current.productImageCount,
          onSyncProductImageNodes,
          1,
          10,
          undefined,
          current.productImageCount + connectedProductCount,
        )}
      </DetailPageFormRow>

      <DetailPageFormRow label="产品卖点">
        <NodeDropdown
          className="w-48"
          value={current.sellingPoints.length > 0 ? 'manual' : 'auto'}
          placeholder="选择创建方式…"
          onChange={(mode) => onSyncSellingPointNode(mode as 'auto' | 'manual')}
          options={[
            { value: 'auto', label: '智能创建' },
            { value: 'manual', label: '手动创建' },
          ]}
        />
      </DetailPageFormRow>

      <DetailPageFormRow label="竞品图">
        {renderStepper(
          current.competitorImageCount,
          (next) => {
            // 数量减到 0 = 跳过竞品分析（skipCompetitorAnalysis 为 toggle 语义，仅在未跳过时调用）
            if (next <= 0) {
              if (!current.skipCompetitor) onSkipCompetitor();
            } else {
              onSyncCompetitorImageNodes(next);
            }
          },
          0,
          9,
          '跳过',
          competitorShown,
        )}
      </DetailPageFormRow>

      {/* P2 竞品风格参考出图开关（默认关防串品，开启后生图请求附带竞品图作风格参考）：仅在未跳过竞品时露出 */}
      {!current.skipCompetitor && (
        <div className="flex items-center gap-4 px-1">
          <div className="flex flex-col gap-0.5 min-w-0 flex-1">
            <span className="text-[24px] font-medium text-white/90 leading-tight">竞品风格参考出图</span>
            <span className="text-[20px] text-white/45">生成图片将参考竞品的配色/版式/质感（不参考其产品与品牌）</span>
          </div>
          <button
            type="button"
            onClick={() => onUpdatePlanner({ useCompetitorStyleReference: !current.useCompetitorStyleReference })}
            onMouseDown={stopPropagation}
            className={`relative w-12 h-[26px] rounded-full transition-colors cursor-pointer shrink-0 ${
              current.useCompetitorStyleReference ? '' : 'bg-white/15'
            }`}
            style={current.useCompetitorStyleReference ? { background: ACCENT } : undefined}
          >
            <span
              className="absolute top-[3px] left-[3px] w-5 h-5 bg-white rounded-full transition-transform"
              style={{ transform: current.useCompetitorStyleReference ? 'translateX(22px)' : 'translateX(0)' }}
            />
          </button>
        </div>
      )}

      <DetailPageFormRow label="风格">{renderStyleRow()}</DetailPageFormRow>
    </section>
    );
  };

  /** CTA 固定在滚动区外：高级设置分组置于按钮上方填满底部空间；展开时节点整体增高（动态高度） */
  const renderConfigFooter = () => (
    <div
      className="shrink-0 px-12 pb-8 pt-3 flex flex-col gap-4"
      onMouseDown={stopPropagation}
    >
      {/* 高级设置：开关行与开始策划同排（左切换、中间分割线、右执行），展开内容有独立视觉容器，与主表单区分层 */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => onUpdatePlanner({ advancedOpen: !advancedOpen })}
            onMouseDown={stopPropagation}
            className="flex items-center gap-2 h-12 text-[22px] text-white/55 hover:text-white/85 transition-colors"
          >
            {advancedOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            高级设置
          </button>
          <div className="flex-1 h-px bg-white/[0.08]" />
          <button
            type="button"
            onClick={onRunPlanner}
            disabled={plannerBusy}
            className="shrink-0 h-12 px-8 rounded-xl text-[20px] font-medium text-white flex items-center gap-2 transition-all hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: ACCENT, boxShadow: `0 8px 24px -8px ${ACCENT}66` }}
          >
            {plannerBusy && <Loader2 size={20} className="animate-spin" />}
            开始策划
          </button>
        </div>

        {advancedOpen && (
          <div
            className="rounded-xl bg-white/[0.03] border border-white/[0.06] px-5 py-5"
            onMouseDown={stopPropagation}
          >
            <DetailPageFormRow label="模型覆盖">
              <NodeDropdown
                value={current.model}
                placeholder={`跟随全局设置（${effectiveModelName}）`}
                onChange={(model) => onUpdatePlanner({ model })}
                options={[
                  { value: '', label: `跟随全局设置（${effectiveModelName}）` },
                  ...(!current.model || models.some((m) => m.id === current.model)
                    ? []
                    : [{ value: current.model, label: current.model }]),
                  ...models.map((model) => ({ value: model.id, label: model.name })),
                ]}
              />
            </DetailPageFormRow>
          </div>
        )}
      </div>
    </div>
  );

  /** 架构策划条目卡（§4.3）：planner 就绪后常驻（PLANNING 后半程与 CONSUME 均显示），点击「查看」打开策划架构面板 */
  const renderPlannerEntry = () => {
    if (!current.isPlannerComplete) return null;
    return (
      <div
        className="rounded-[20px] bg-white/[0.05] px-7 py-5 flex items-center gap-4"
        onMouseDown={stopPropagation}
      >
        <div className="flex flex-col gap-0.5 min-w-0">
          <span className="text-[24px] font-medium text-white/90 leading-tight">架构策划</span>
          <span className="text-[20px] text-white/45 truncate">
            产品识别 · 竞品分析 · 策划文案 · 详情页架构
          </span>
        </div>
        <span className="flex-1 min-w-0" />
        <button
          type="button"
          onClick={() => setPlannerPanelOpen(true)}
          className="shrink-0 h-14 px-7 rounded-full border border-white/20 text-[20px] text-white/85 hover:border-[#6D5BF5] hover:text-white transition-all"
        >
          查看
        </button>
      </div>
    );
  };

  /** 执行态两端结构（§4.1.1）：信息区贴顶、架构条目居中、贴底执行条经 mt-auto 压底 */
  const renderRunning = () => {
    const isPlanner = stage === DetailPageStage.PLANNING;
    const runState = isPlanner ? effectivePlannerRunState : effectiveDesignerRunState;
    const session = isPlanner ? plannerSession : designerSession;
    return (
      <section className="flex flex-col flex-1 gap-6">
        {renderSummaryPills()}
        {renderPlannerEntry()}
        <DetailPageSkillPanel
          runState={runState}
          stage={session.stage ?? { label: '技能执行中', detail: isPlanner ? 'page_design' : 'page_prompt' }}
          onCancel={session.cancel}
          onRetry={isPlanner ? onRunPlanner : onRunDesigner}
        />
      </section>
    );
  };

  /** CONSUME 态（§4.6）：架构条目常驻 + 分屏区「标题行/全量列表/常驻按钮行」三段式，节点高度自适应全量展示，行内不折叠（查看经弹窗） */
  const renderConsume = () => {
    const generatedCount = generatedBlocks.length;
    const allGenerated = prompts.length > 0 && generatedCount >= prompts.length;
    return (
      <section className="flex flex-col gap-5">
        {renderPlannerEntry()}

        <div className="flex flex-col gap-5" onMouseDown={stopPropagation}>
          <div className="flex items-center gap-3 px-1">
            <span className="text-[24px] font-medium text-white/90">分屏提示词</span>
            <span className="text-[20px] text-white/50">
              {generatedCount} / {prompts.length} 已出图
            </span>
          </div>

          {prompts.length === 0 ? (
            <div className="rounded-[20px] bg-white/[0.04] px-6 py-6 flex flex-col items-center gap-3">
              <span className="text-[20px] text-white/45">未解析到分屏提示词，请重新生成</span>
              <button
                type="button"
                onClick={onRegeneratePrompts}
                className="h-12 px-6 rounded-xl bg-white/[0.06] text-[20px] text-white/80 hover:bg-white/[0.10] hover:text-white transition-all"
              >
                重新生成
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {prompts.map((block) => {
                const generated = generatedBlocks.includes(block.index);
                const displayTitle = resolveBlockDisplayTitle(block);
                return (
                  <div
                    key={block.index}
                    className="rounded-[20px] bg-white/[0.04] hover:bg-white/[0.07] transition-colors"
                  >
                    <div
                      onMouseDown={stopPropagation}
                      className="flex items-center gap-3 px-5 py-3 select-none min-w-0"
                    >
                      <span
                        className="w-8 h-8 rounded-full flex items-center justify-center text-[18px] font-medium shrink-0"
                        style={{ background: `${ACCENT}26`, color: ACCENT }}
                        title={UNIT_TYPE_LABELS[block.unitType]}
                      >
                        {block.unitType}
                      </span>
                      <span className="text-[22px] font-medium text-white/90 truncate flex-1 min-w-0">
                        {formatBlockLabel(block.index)}
                        {displayTitle && (
                          <span className="font-normal text-white/45"> · {displayTitle}</span>
                        )}
                      </span>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setViewingPrompt(block); }}
                        className="px-5 h-9 rounded-full border border-white/20 text-[18px] text-white/80 hover:border-[#6D5BF5] hover:text-white transition-all shrink-0"
                      >
                        查看
                      </button>
                      {generated ? (
                        <span className="flex items-center gap-1.5 px-4 h-9 rounded-full bg-emerald-400/10 text-[18px] text-emerald-300 shrink-0">
                          <Check size={16} />
                          已出图
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onGenerateBlock(block); }}
                          className="px-5 h-9 rounded-full border border-white/20 text-[18px] text-white/80 hover:border-[#6D5BF5] hover:text-white transition-all shrink-0"
                        >
                          生成出图
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* 底部操作区：高级设置开关居左（原说明文案位置，中间分割线、展开容器在下方、逻辑与配置态一致），「重新生成 + 批量生图」靠右成组，批量生图贴最右 */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-4 flex-wrap">
              <button
                type="button"
                onClick={() => onUpdatePlanner({ advancedOpen: !advancedOpen })}
                className="flex items-center gap-2 h-12 text-[22px] text-white/55 hover:text-white/85 transition-colors"
              >
                {advancedOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                高级设置
              </button>
              <div className="flex-1 h-px bg-white/[0.08]" />
              {confirmRegenerate ? (
                <div className="flex items-center gap-3 h-12 px-5 rounded-xl bg-white/[0.06] border border-white/[0.10]">
                  <span className="text-[18px] text-white/70">将覆盖现有分屏提示词与出图记录？</span>
                  <button
                    type="button"
                    onClick={() => {
                      setConfirmRegenerate(false);
                      onRegeneratePrompts();
                    }}
                    className="h-9 px-4 rounded-lg text-[18px] font-medium text-white transition-all hover:brightness-110"
                    style={{ background: ACCENT }}
                  >
                    确认
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmRegenerate(false)}
                    className="h-9 px-4 rounded-lg text-[18px] text-white/60 hover:bg-white/[0.08] hover:text-white/90 transition-all"
                  >
                    取消
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmRegenerate(true)}
                  className="h-12 px-8 rounded-xl bg-white/[0.06] text-[20px] text-white/80 hover:bg-white/[0.10] hover:text-white transition-all"
                >
                  重新生成
                </button>
              )}
              <button
                type="button"
                onClick={onGenerateAll}
                disabled={allGenerated}
                className="h-12 px-8 rounded-xl text-[20px] font-medium text-white transition-all hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: ACCENT, boxShadow: `0 8px 24px -8px ${ACCENT}66` }}
              >
                批量生图
              </button>
            </div>

            {advancedOpen && (
              <div
                className="rounded-xl bg-white/[0.03] border border-white/[0.06] px-5 py-5"
                onMouseDown={stopPropagation}
              >
                <DetailPageFormRow label="生图模型">
                  <NodeDropdown
                    value={current.imageModel}
                    placeholder={`跟随默认（${defaultImageModelName}）`}
                    onChange={(imageModel) => onUpdatePlanner({ imageModel })}
                    options={[
                      { value: '', label: `跟随默认（${defaultImageModelName}）` },
                      ...(!current.imageModel || imageModels.some((m) => m.id === current.imageModel)
                        ? []
                        : [{ value: current.imageModel, label: current.imageModel }]),
                      ...imageModels.map((model) => ({ value: model.id, label: model.name })),
                    ]}
                  />
                </DetailPageFormRow>
              </div>
            )}
          </div>
        </div>
      </section>
    );
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      {renderHead()}
      {isRunningStage && renderRail()}
      <div className="flex-1 min-h-0 overflow-y-auto px-12 pt-2 pb-8 flex flex-col gap-6">
        {isConfig && renderConfigBody()}
        {isRunningStage && renderRunning()}
        {isConsume && renderConsume()}
      </div>
      {isConfig && renderConfigFooter()}
      {viewingPrompt && (
        <SplitPromptFullscreenView
          prompts={prompts}
          activeIndex={viewingPrompt.index}
          onSelect={(index) => {
            const next = prompts.find((p) => p.index === index);
            if (next) setViewingPrompt(next);
          }}
          onClose={() => setViewingPrompt(null)}
        />
      )}
      <PlannerResultPanel
        open={plannerPanelOpen}
        onClose={() => setPlannerPanelOpen(false)}
        planner={current}
        nodeTitle={nodeTitle}
        pendingConfirmation={styleConfirmPending}
        onConfirm={
          styleConfirmPending
            ? () => {
                plannerSession.reply('确认', undefined, '确认');
                setPlannerPanelOpen(false);
              }
            : undefined
        }
        onDeleteScreen={
          current.competitorScreens && current.competitorScreens.length > 0
            ? (index) => {
                const rest = (current.competitorScreens ?? [])
                  .filter((screen) => screen.index !== index)
                  .sort((a, b) => a.topPct - b.topPct)
                  .map((screen, i) => ({ ...screen, index: i + 1 }));
                onUpdatePlanner({ competitorScreens: rest });
              }
            : undefined
        }
      />
    </div>
  );
};

export const DetailPagePipelineView = memo(DetailPagePipelineViewInner, (prev, next) => {
  return (
    prev.stage === next.stage &&
    prev.planner === next.planner &&
    prev.designer === next.designer &&
    prev.generatedBlocks === next.generatedBlocks &&
    prev.plannerSession.runState === next.plannerSession.runState &&
    prev.plannerSession.stage === next.plannerSession.stage &&
    prev.designerSession.runState === next.designerSession.runState &&
    prev.designerSession.stage === next.designerSession.stage &&
    prev.onUpdatePlanner === next.onUpdatePlanner &&
    prev.onSyncProductImageNodes === next.onSyncProductImageNodes &&
    prev.onSyncCompetitorImageNodes === next.onSyncCompetitorImageNodes &&
    prev.onSkipCompetitor === next.onSkipCompetitor &&
    prev.onSyncSellingPointNode === next.onSyncSellingPointNode &&
    prev.onRunPlanner === next.onRunPlanner &&
    prev.onRunDesigner === next.onRunDesigner &&
    prev.onGenerateBlock === next.onGenerateBlock &&
    prev.onGenerateAll === next.onGenerateAll &&
    prev.onRegeneratePrompts === next.onRegeneratePrompts &&
    prev.nodeTitle === next.nodeTitle
  );
});
